import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { API_BASE } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, FileText, Clock, CheckCircle2, Download } from "lucide-react";

export default function CarrierGuidePage() {
  const { carrierId } = useParams();
  const [guide, setGuide] = useState(null);

  useEffect(() => {
    api
      .get(`/carriers/${carrierId}/guide`)
      .then((r) => setGuide(r.data))
      .catch(() => {});
  }, [carrierId]);

  if (!guide) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-12 text-gray-500">
        Loading guide…
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-12" data-testid="carrier-guide-page">
      <Link
        to="/dashboard"
        className="inline-flex items-center text-sm text-gray-600 hover:text-[#1E3F33] mb-6"
      >
        <ArrowLeft className="w-4 h-4 mr-1" /> Back to dashboard
      </Link>

      <div className="flex items-start justify-between gap-6 mb-2">
        <div>
          <p className="text-xs uppercase tracking-widest text-[#C25934] font-medium mb-2">
            Carrier guide
          </p>
          <h1 className="font-['Outfit'] text-4xl font-semibold tracking-tight">
            {guide.name}
          </h1>
        </div>
        <a
          href={`${API_BASE}/carriers/${carrierId}/playbook.pdf`}
          target="_blank"
          rel="noreferrer"
          className="shrink-0 inline-flex items-center gap-2 text-sm font-medium rounded-full border border-[#1E3F33] text-[#1E3F33] bg-white hover:bg-[#1E3F33] hover:text-[#F9F8F6] px-5 py-2 transition-colors"
          data-testid="download-carrier-playbook"
        >
          <Download className="w-4 h-4" />
          Download playbook PDF
        </a>
      </div>

      <Card className="border-[#E5E7EB] mt-8">
        <CardContent className="p-8 space-y-6">
          <div className="flex items-start gap-4">
            <Clock className="w-5 h-5 text-[#C25934] mt-1" />
            <div>
              <p className="font-['Outfit'] font-semibold mb-1">
                Proof of Loss window
              </p>
              <p className="text-gray-600">
                {guide.proof_of_loss_days} days from date of loss.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <FileText className="w-5 h-5 text-[#C25934] mt-1" />
            <div>
              <p className="font-['Outfit'] font-semibold mb-1">
                What they ask for
              </p>
              <p className="text-gray-700 leading-relaxed">{guide.guide}</p>
            </div>
          </div>

          {guide.required_photos?.length > 0 && (
            <div className="flex items-start gap-4">
              <CheckCircle2 className="w-5 h-5 text-[#1E3F33] mt-1" />
              <div>
                <p className="font-['Outfit'] font-semibold mb-1">
                  Required photos
                </p>
                <ul
                  className="text-gray-700 space-y-1.5 list-disc list-inside"
                  data-testid="required-photos-list"
                >
                  {guide.required_photos.map((p, i) => (
                    <li key={`photo-${i}-${p}`}>{p}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {guide.required_docs?.length > 0 && (
            <div className="flex items-start gap-4">
              <FileText className="w-5 h-5 text-[#1E3F33] mt-1" />
              <div>
                <p className="font-['Outfit'] font-semibold mb-1">
                  Required documents
                </p>
                <ul
                  className="text-gray-700 space-y-1.5 list-disc list-inside"
                  data-testid="required-docs-list"
                >
                  {guide.required_docs.map((d, i) => (
                    <li key={`doc-${i}-${d}`}>{d}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {guide.tips?.length > 0 && (
            <div className="flex items-start gap-4">
              <CheckCircle2 className="w-5 h-5 text-[#C25934] mt-1" />
              <div>
                <p className="font-['Outfit'] font-semibold mb-1">
                  Pro tips
                </p>
                <ul
                  className="text-gray-700 space-y-1.5 list-disc list-inside"
                  data-testid="tips-list"
                >
                  {guide.tips.map((t, i) => (
                    <li key={`tip-${i}-${t}`}>{t}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
